<template>
  <div>
    <div
      v-for="actionParam in visibleParams"
      :key="actionParam.Id"
      class="flex flex-col gap-1"
      v-if="data"
    >
      <FormField
        v-model:value="data[actionParam.Key]"
        v-model:actual="data[`_${actionParam.Key}`]"
        v-model:form="data"
        :data-key="actionParam.Key"
        :id="actionParam.Key"
        :input-type="getControlTypeMapping(actionParam.ControlType ?? '')"
        :placeholder="actionParam.FieldLabel"
        :label="actionParam.FieldLabel"
        :validation="actionParam.Validation"
        :required="isRequiredField(actionParam)"
        :showStar="isRequiredField(actionParam)"
        :errors="validationErrors ? [validationErrors[actionParam.Key]] : []"
        :misc="{
          fetchOptions: true,
          displayKey: actionParam.DisplayKeyName,
          valueKey: actionParam.ValueKeyName,
          endpoint: actionParam.EndpointRoute,
          filter: cascadeFilter(actionParam.CascadeFrom ?? ''),
        }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { InputType } from "~/scad-shared/types/generic";
import type { IMetaAction, IMetaActionParameter } from "~/scad-shared/types/scad";

const props = defineProps<{
  action: IMetaAction;
  actionParams: IMetaActionParameter[];
}>();

const data = defineModel<Indexable | null>("data", {
  default: () => ({}),
});

function getControlTypeMapping(ControlType: string): InputType | undefined {
  // @ts-ignore
  return ControlTypeToGenericInputType(ControlType);
}
// SHOW/HIDE LOGIC
const evaluator = new ExpressionEvaluator();
const evaluateExpression: ComputedRef<(expression: string) => boolean> =
  computed(() => {
    return (expression: string) => {
      return evaluator.evaluateExpression(expression, data.value ?? {});
    };
  });

const showField = computed<(param: IMetaActionParameter) => boolean>(() => {
  return (param: IMetaActionParameter) => {
    const evaluationResult: boolean = param.ShowExpression
      ? evaluateExpression.value(param.ShowExpression)
      : true;

    return evaluationResult;
  };
});

const visibleParams = computed<IMetaActionParameter[]>(() => {
  return props.actionParams
    .filter((param) => showField.value(param))
    .sort((a, b) => {
      return (a.SortOrder ?? 0) - (b.SortOrder ?? 0);
    });
});

// VALIDATION LOGIC
const validationRulesMap = getValidationRulesMapFromMetaActionParameters(
  props.actionParams
);
const validationRules = computed<ValidationRule[]>(() => {
  const rules: ValidationRule[] = [];

  visibleParams.value.forEach((param) => {
    if (validationRulesMap[param.Key]) {
      rules.push(validationRulesMap[param.Key]);
    }
  });

  return rules;
});

const validation = computed(() => {
  return new ValidationEngine(validationRules.value);
});

function isRequiredField(param: IMetaActionParameter): boolean {
  if (param.Validation) {
    return !validationRulesMap[param.Key].schema.spec.optional;
  } else {
    return false;
  }
}

const validationErrors = defineModel<{ [key: string]: string }>(
  "validationErrors",
  {
    default: {},
  }
);

async function validate(throwOnError: boolean = false) {
  const validationRecord = { ...data.value };

  validationErrors.value = await validation.value.validateToString(
    validationRecord
  );

  if (throwOnError && Object.keys(validationErrors.value).length > 0) {
    let errors: string[] = [];
    for (let field in validationErrors.value) {
      errors.push(validationErrors.value[field]);
    }
    throw new Error(errors.join(".\n ") + ".");
  }
  return validationErrors.value;
}

defineExpose({
  validate,
});


// CASCADING LOGIC
const cascadeFilter = computed(() => {
  return (cascadeFrom: string) => {
    const filter: { [key: string]: any } = {};
    if (!cascadeFrom || cascadeFrom.trim() === "") return filter;
    const cascades = [];
    if (cascadeFrom.includes(",")) {
      cascades.push(...cascadeFrom.split(","));
    } else {
      cascades.push(cascadeFrom);
    }

    cascades.forEach((cascade) => {
      if (!data.value) return;
      let selectedValue: any = cascade
        .trim()
        .split(".")
        .reduce((a, b) => a[b], data.value);

      //the user has selected an option in the cascade parent
      //console.log('cascading on ', cascade,data.value[cascade]);
      filter[cascade] = data.value[cascade];
    });

    return filter;
  };
});

// WATCHER BASED ON CASCADING LOGIC TO CLEAR FIELDS ON CASCADE
// Get all unique cascade sources
const cascadeSources = computed(() => {
  const sources = new Set<string>();
  props.actionParams.forEach((param) => {
    if (param.CascadeFrom) {
      param.CascadeFrom.split(",").forEach((c) => {
        sources.add(c.trim());
      });
    }
  });
  return Array.from(sources);
});

watch(
  () => cascadeSources.value.map((source) => data.value?.[source]),
  () => {
    if (!data.value) return;
    props.actionParams.forEach((param) => {
      if (param.CascadeFrom && data.value) {
        data.value[param.Key] = null;
      }
    });
  }
);
</script>

<style scoped></style>
